import { useEffect, useState } from "react";
import {
  Button,
  Form,
  FormGroup,
  Input,
  Label,
  Offcanvas,
  OffcanvasBody,
  OffcanvasHeader,
} from "../Common/AppOverlay";
import {
  createUpdateClient,
  getNextClientCode,
} from "../../helpers/backend_helper";
import { ErrorNotify, SuccessNotify } from "../../shared/Alert";
import "./NewClientDrawer.scss";

const blankClient = {
  name: "",
  code: "",
  type: "",
  industry: "",
  contact: "",
  email: "",
  phone: "",
  risk: "",
  status: "Active",
  notes: "",
};

/**
 * Shared create-client drawer. onSaved receives the persisted client record,
 * allowing a calling workflow to select it immediately.
 */
const NewClientDrawer = ({
  clientTypes = [],
  industryTypes = [],
  isOpen,
  onClose,
  onSaved,
}) => {
  const [form, setForm] = useState(blankClient);
  const [errors, setErrors] = useState({});
  const [isGeneratingCode, setIsGeneratingCode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return undefined;
    let active = true;
    setForm(blankClient);
    setErrors({});
    setIsGeneratingCode(true);

    getNextClientCode()
      .then((response) => {
        if (!active) return;
        if (response?.status) {
          setForm({ ...blankClient, code: response.data || "" });
          return;
        }
        ErrorNotify(response?.message || "Unable to generate a client code.");
      })
      .catch(() => {
        if (active) ErrorNotify("Unable to generate a client code.");
      })
      .finally(() => {
        if (active) setIsGeneratingCode(false);
      });

    return () => {
      active = false;
    };
  }, [isOpen]);

  const updateForm = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  };

  const saveClient = async (event) => {
    event.preventDefault();
    if (isSaving) return;

    const nextErrors = {};
    ["name", "code", "type", "contact", "email"].forEach((field) => {
      if (!form[field].trim()) nextErrors[field] = "Required";
    });
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      return;
    }

    setIsSaving(true);
    try {
      const response = await createUpdateClient({
        id: 0,
        clientName: form.name.trim(),
        clientCode: form.code.trim(),
        clientTypeId: Number(form.type),
        industryTypeId: form.industry ? Number(form.industry) : null,
        primaryContactName: form.contact.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        riskLevel: form.risk || "Low",
        isActive: form.status === "Active",
        notes: form.notes.trim() || null,
      });
      if (!response?.status || !response.data) {
        ErrorNotify(response?.message || "Unable to create client.");
        return;
      }

      SuccessNotify(response.message || "Client created successfully.");
      onSaved?.(response.data);
      onClose();
    } catch {
      ErrorNotify("Unable to create client.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Offcanvas
      backdrop="static"
      backdropClassName="new-client-drawer__backdrop"
      className="new-client-drawer"
      direction="end"
      isOpen={isOpen}
      toggle={onClose}
    >
      <OffcanvasHeader toggle={onClose}>New Client</OffcanvasHeader>
      <OffcanvasBody>
        <p className="new-client-drawer__intro">
          Enter client information to create a new client record.
        </p>
        <Form className="new-client-form" onSubmit={saveClient}>
          <h3>
            <i aria-hidden="true" className="bx bx-buildings" /> General
            Information
          </h3>
          <FormGroup>
            <Label>
              Client Name <b>*</b>
            </Label>
            <Input
              invalid={Boolean(errors.name)}
              name="name"
              onChange={updateForm}
              placeholder="Enter client name"
              value={form.name}
            />
            {errors.name && <small>{errors.name}</small>}
          </FormGroup>
          <FormGroup>
            <Label>
              Client Code <b>*</b>
            </Label>
            <Input
              invalid={Boolean(errors.code)}
              name="code"
              onChange={updateForm}
              placeholder="Enter unique client code"
              value={form.code}
            />
            {errors.code && <small>{errors.code}</small>}
            <small className="new-client-code-help">
              {isGeneratingCode
                ? "Generating client code…"
                : "Generated automatically. You can change it if needed."}
            </small>
          </FormGroup>
          <FormGroup>
            <Label>
              Client Type <b>*</b>
            </Label>
            <Input
              invalid={Boolean(errors.type)}
              name="type"
              onChange={updateForm}
              type="select"
              value={form.type}
            >
              <option value="">Select client type</option>
              {clientTypes.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.name}
                </option>
              ))}
            </Input>
            {errors.type && <small>{errors.type}</small>}
          </FormGroup>
          <FormGroup>
            <Label>Industry</Label>
            <Input
              name="industry"
              onChange={updateForm}
              type="select"
              value={form.industry}
            >
              <option value="">Select industry</option>
              {industryTypes.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.name}
                </option>
              ))}
            </Input>
          </FormGroup>
          <h3>
            <i aria-hidden="true" className="bx bx-user" /> Contact Information
          </h3>
          <FormGroup>
            <Label>
              Primary Contact Name <b>*</b>
            </Label>
            <Input
              invalid={Boolean(errors.contact)}
              name="contact"
              onChange={updateForm}
              placeholder="Enter contact name"
              value={form.contact}
            />
            {errors.contact && <small>{errors.contact}</small>}
          </FormGroup>
          <FormGroup>
            <Label>
              Email <b>*</b>
            </Label>
            <Input
              invalid={Boolean(errors.email)}
              name="email"
              onChange={updateForm}
              placeholder="Enter email address"
              type="email"
              value={form.email}
            />
            {errors.email && <small>{errors.email}</small>}
          </FormGroup>
          <FormGroup>
            <Label>Phone</Label>
            <Input
              name="phone"
              onChange={updateForm}
              placeholder="Enter phone number"
              value={form.phone}
            />
          </FormGroup>
          <h3>
            <i aria-hidden="true" className="bx bx-detail" /> Additional
            Information
          </h3>
          <FormGroup>
            <Label>Risk Level</Label>
            <Input
              name="risk"
              onChange={updateForm}
              type="select"
              value={form.risk}
            >
              <option value="">Select risk level</option>
              <option value="Low">Low</option>
              <option value="Medium">Medium</option>
              <option value="High">High</option>
            </Input>
          </FormGroup>
          <FormGroup>
            <Label>Status</Label>
            <Input
              name="status"
              onChange={updateForm}
              type="select"
              value={form.status}
            >
              <option>Active</option>
              <option>Inactive</option>
            </Input>
          </FormGroup>
          <FormGroup>
            <Label>Notes</Label>
            <Input
              name="notes"
              onChange={updateForm}
              placeholder="Enter any notes about this client..."
              rows="4"
              type="textarea"
              value={form.notes}
            />
          </FormGroup>
          <div className="new-client-drawer__footer">
            <Button disabled={isSaving} onClick={onClose} outline type="button">
              Cancel
            </Button>
            <Button color="primary" disabled={isSaving} type="submit">
              {isSaving && (
                <span
                  aria-hidden="true"
                  className="spinner-border spinner-border-sm"
                />
              )}
              {isSaving ? "Creating client…" : "Create Client"}
            </Button>
          </div>
        </Form>
      </OffcanvasBody>
    </Offcanvas>
  );
};

export default NewClientDrawer;
